import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';

const RegisterPage = () => {
  const { signUp } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptTerms) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const { error: signUpError } = await signUp(email, password, fullName);
      if (signUpError) {
        if (signUpError.message === 'User already registered') {
          setError('Un compte existe déjà avec cet email. Veuillez vous connecter.');
        } else {
          setError('Une erreur est survenue : ' + signUpError.message);
        }
      } else {
        setSuccess('Compte créé ! Vérifiez votre email pour confirmer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8"
      >
        {/* Logo */}
        <div className="text-center space-y-3">
          <img src="/logo-blanc.svg" alt="Consentink" className="h-12 mx-auto object-contain" />
          <p className="text-muted-foreground">Gestion professionnelle des consentements</p>
        </div>

        <form onSubmit={handleSubmit} className="card-elevated p-8 space-y-5">
          <h2 className="text-xl font-bold font-display text-center">Créer un compte</h2>

          {/* First name + Last name */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Prénom</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                  placeholder="Jean"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                  placeholder="Dupont"
                  required
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                placeholder="email@exemple.com"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-4 bg-background border-2 border-border rounded-xl text-base focus:border-primary focus:outline-none transition-colors"
                placeholder="••••••••"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* CGU checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer"
              required
            />
            <label htmlFor="acceptTerms" className="text-sm text-muted-foreground cursor-pointer">
              J'accepte les{' '}
              <a href="https://consentink.fr/cgu" target="_blank" rel="noreferrer" className="text-primary underline">
                Conditions Générales d'Utilisation
              </a>
            </label>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-destructive text-sm text-center">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-xl p-3 text-[hsl(var(--success))] text-sm text-center">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !acceptTerms}
            className="touch-button-lg w-full bg-primary text-primary-foreground rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              'Créer mon compte'
            )}
          </button>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Déjà un compte ?{' '}</span>
            <Link to="/auth/login" className="text-primary font-semibold">
              Se connecter
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
